// Shift Logbook — handover notes with pins + per-user "acknowledged" ticks (Dashboard card).
import React, { useCallback, useEffect, useState } from 'react';
import { BookOpen, Check, Loader2, Pin, PinOff, Send, Trash2 } from 'lucide-react';
import { apiCall } from './_shared';

const CATS = [
  { k: 'general', label: 'General', cls: 'bg-slate-100 text-slate-700' },
  { k: 'handover', label: 'Handover', cls: 'bg-orange-100 text-orange-700' },
  { k: 'guest', label: 'Guest', cls: 'bg-sky-100 text-sky-700' },
  { k: 'maintenance', label: 'Maintenance', cls: 'bg-amber-100 text-amber-700' },
  { k: 'cash', label: 'Cash', cls: 'bg-emerald-100 text-emerald-700' },
];
const catCls = (k) => (CATS.find((c) => c.k === k) || CATS[0]).cls;
const fmt = (iso) => (iso || '').slice(5, 16).replace('T', ' ');

const Entry = ({ e, me, canLead, onChange, highlight }) => {
  const [busy, setBusy] = useState(false);
  const act = async (fn) => { setBusy(true); try { await fn(); await onChange(); } catch (err) { alert(err.message); } finally { setBusy(false); } };
  const mine = e.author === me;
  return (
    <li className={`py-2 px-2 rounded-lg ${highlight ? 'bg-yellow-50 ring-1 ring-yellow-300' : e.pinned ? 'bg-orange-50/60' : ''}`} data-testid={`logbook-entry-${e.id}`}>
      <div className="flex items-start gap-2">
        <button
          onClick={() => act(() => apiCall(`/api/admin/logbook/${e.id}/ack`, { method: e.acked_by_me ? 'DELETE' : 'POST' }))}
          disabled={busy}
          title={e.acked_by_me ? 'Un-acknowledge' : 'Acknowledge — I have read this'}
          className={`mt-0.5 w-5 h-5 rounded-full border flex items-center justify-center flex-shrink-0 transition-colors ${e.acked_by_me ? 'bg-emerald-500 border-emerald-500 text-white' : 'border-slate-300 hover:border-emerald-400'}`}
          data-testid={`logbook-ack-${e.id}`}
        >
          {e.acked_by_me && <Check className="w-3 h-3" />}
        </button>
        <div className="flex-1 min-w-0">
          <p className="text-sm text-slate-800 whitespace-pre-wrap break-words">{e.text}</p>
          <p className="text-[11px] text-slate-500 mt-0.5 flex flex-wrap items-center gap-x-2">
            <span className={`px-1.5 py-0.5 rounded ${catCls(e.category)}`}>{e.category}</span>
            <span><b>{e.author}</b> · {e.shift} · {fmt(e.created_at)}</span>
            {e.acks?.length > 0 && <span title={e.acks.map((a) => a.by).join(', ')}>✓ {e.acks.length} read</span>}
          </p>
        </div>
        <div className="flex items-center gap-0.5 flex-shrink-0">
          {(canLead || mine) && (
            <button onClick={() => act(() => apiCall(`/api/admin/logbook/${e.id}/pin`, { method: 'PUT' }))} disabled={busy} className="p-1 rounded hover:bg-slate-100" title={e.pinned ? 'Unpin' : 'Pin to top'} data-testid={`logbook-pin-${e.id}`}>
              {e.pinned ? <PinOff className="w-3.5 h-3.5 text-orange-500" /> : <Pin className="w-3.5 h-3.5 text-slate-400" />}
            </button>
          )}
          {(canLead || mine) && (
            <button onClick={() => { if (window.confirm('Delete this note?')) act(() => apiCall(`/api/admin/logbook/${e.id}`, { method: 'DELETE' })); }} disabled={busy} className="p-1 rounded hover:bg-red-50" title="Delete" data-testid={`logbook-delete-${e.id}`}>
              <Trash2 className="w-3.5 h-3.5 text-red-400" />
            </button>
          )}
        </div>
      </div>
    </li>
  );
};

const ShiftLogbook = ({ currentUser, highlightId }) => {
  const [data, setData] = useState(null);
  const [text, setText] = useState('');
  const [cat, setCat] = useState('handover');
  const [pin, setPin] = useState(false);
  const [posting, setPosting] = useState(false);
  const me = currentUser?.username;
  const canLead = ['super_admin', 'admin', 'manager'].includes(currentUser?.role);

  const load = useCallback(async () => {
    try { setData(await apiCall('/api/admin/logbook?limit=40')); } catch (_) { /* card must never break the dashboard */ }
  }, []);
  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, [load]);

  const post = async () => {
    if (!text.trim()) return;
    setPosting(true);
    try {
      await apiCall('/api/admin/logbook', { method: 'POST', body: JSON.stringify({ text: text.trim(), category: cat, pinned: pin }) });
      setText(''); setPin(false);
      await load();
    } catch (e) { alert(e.message); }
    finally { setPosting(false); }
  };

  const entries = (data?.entries || []).map((e) => (
    <Entry key={e.id} e={e} me={me} canLead={canLead} onChange={load} highlight={highlightId === e.id} />
  ));

  return (
    <div className="bg-white rounded-xl p-4 shadow-sm border flex flex-col min-h-0" data-testid="shift-logbook">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
          <BookOpen className="w-4 h-4 text-orange-500" /> Shift logbook
          {data?.unacked_count > 0 && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-red-500 text-white" data-testid="logbook-unacked">{data.unacked_count} unread</span>}
        </h3>
        <span className="text-[11px] text-slate-500 capitalize">{data?.current_shift} shift</span>
      </div>
      <div className="flex gap-1.5 mb-2">
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) post(); }}
          rows={2}
          placeholder="Handover note for the next shift… (Ctrl+Enter to post)"
          className="flex-1 border rounded-lg px-2 py-1.5 text-sm resize-none"
          data-testid="logbook-text"
        />
        <div className="flex flex-col gap-1">
          <select value={cat} onChange={(e) => setCat(e.target.value)} className="border rounded-lg px-1.5 py-1 text-xs" data-testid="logbook-category">
            {CATS.map((c) => <option key={c.k} value={c.k}>{c.label}</option>)}
          </select>
          <label className="flex items-center gap-1 text-[11px] text-slate-600 px-0.5"><input type="checkbox" checked={pin} onChange={(e) => setPin(e.target.checked)} data-testid="logbook-pin-new" /> Pin</label>
          <button onClick={post} disabled={posting || !text.trim()} className="px-2 py-1 rounded-lg bg-slate-900 text-white text-xs font-semibold flex items-center justify-center gap-1 disabled:opacity-50" data-testid="logbook-post">
            {posting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />} Post
          </button>
        </div>
      </div>
      <ul className="divide-y flex-1 overflow-y-auto min-h-0 -mx-1" data-testid="logbook-list">
        {entries.length ? entries : <li className="py-6 text-center text-xs text-slate-400">No notes yet — leave the next shift a heads-up.</li>}
      </ul>
    </div>
  );
};

export default ShiftLogbook;
